/**
 * Listas do CRM — motivos de perda, origem do lead, motivos de descarte.
 *
 * Traz junto os três editores que só ela usa (`ListaEditor`,
 * `MotivosDescarteEditor`, `PrazoRetorno`).
 *
 * ── Extraído da `ConfigCrmPage` em 2026-08-03 (K-3)
 *
 * A `ConfigCrmPage` tinha **1905 linhas** e 10 abas num arquivo só. Cada aba
 * virou um item do menu da Configurações, e o painel da casca renderiza este
 * componente direto — sem passar por ela.
 *
 * A extração é passo PRÓPRIO, antes da migração, de propósito: separar "mover
 * código" de "mudar comportamento" é o que permite ler o diff de cada um.
 */
/**
 * CRM v2 — Configurações > Comercial > CRM (PRD §7).
 *
 * TODO parâmetro do Comercial mora aqui: catálogo de serviços · fases ·
 * status de lead · origens · campos obrigatórios por fase · tipos de
 * atividade · motivos de perda/descarte · documentos do Ganho · tags ·
 * construtor de automações · visibilidade por perfil · carteira (ownership).
 * (Metas têm tela própria em Comercial → Metas.)
 */

import {useEffect, useState} from 'react';

import {Badge, Button, Card, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, HelpTip, Input, Label, Select, SelectOption, useConfirmDialog, useToast} from '@/components/ui';
import {cn} from '@/lib/cn';
import {mensagemErroConfig} from './erro-config';
import {GradeSimbolos, SIMBOLO_PADRAO, SimboloAtividade} from '../simbolos-atividade';
import {rotuloMinutos, SEM_LEMBRETE_PADRAO} from '@/lib/crm/lembrete-rotulo';
import type {
  MotivoSimples,
} from '@/lib/crm/crm-types';
import {useCatalogoServicos, useConfigActions, useCrmTags, useLeadStatus, useMotivosDescarte, useMotivosPerda, useDocumentosExigidos, useOrigensLead, useReciclagemDestino, useSalvarReciclagemDestino, useTiposAtividadeCrm, type RetornoDescarteDestino} from '@/lib/crm/crm-store';
// O14: dropdowns de usuário do comercial listam SÓ o departamento comercial.
// O7: config completa do Orçamento (markups, régua, faixas, kits, taxas).
// A7: Modelos de orçamento nomeados (aba própria — grid de cards + drawer).
// R3: Reciclagem de leads (aba própria — leads parados voltam pro pool/redistribuição).
// Bloco C: Lembretes de atividade agendada (liga/desliga + antecedência).


export function AbaListas() {
  /**
   * 🔴 02/10/2026 — TODAS pedem os DESATIVADOS, e isto era um bug.
   *
   * ⚠️ Só o catálogo de serviços pedia. As outras sete escondiam o que tinha
   * sido desativado — e, como o apagar é SOFT, **o item sumia da tela e não
   * havia como religá-lo.** Medido no beta: 6 itens presos assim.
   *
   * ✅ Esta é a tela que ADMINISTRA as listas: aqui se vê tudo, e cada item
   * desativado vem **marcado** como tal. ❌ As telas que CONSOMEM (o card, o
   * lead, o funil) continuam sem os inativos — lá eles não são escolha.
   */
  const servicos = useCatalogoServicos(true);
  const origens = useOrigensLead(true);
  const leadStatus = useLeadStatus(true);
  const motivosPerda = useMotivosPerda(true);
  const motivosDescarte = useMotivosDescarte(true);
  const documentos = useDocumentosExigidos(true);
  const tipos = useTiposAtividadeCrm(true);
  const tags = useCrmTags(true);

  return (
    // U6: grid 2–3 colunas — todas as listas visíveis aproveitando a largura,
    // cartões com estilo/altura padronizados (lista interna rola sozinha).
    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
      <ListaEditor
        titulo="Catálogo de serviços"
        help="A lista de serviços que a IGM vende. Pontual = venda única (ex.: instalação). Recorrente = cobrança que se repete todo mês (ex.: monitoramento)."
        descricao="Fonte única do card e das metas. Cada serviço é Pontual ou Recorrente."
        lista="catalogo-servicos"
        itens={(servicos.data ?? []).map((s) => ({ id: s.id, nome: s.nome, extra: s.tipo === 'recorrente' ? 'Recorrente' : 'Pontual', ativo: s.ativo }))}
        camposExtras={[{ key: 'tipo', label: 'Tipo', tipo: 'select', opcoes: [['pontual', 'Pontual'], ['recorrente', 'Recorrente']] }]}
      />
      <ListaEditor
        titulo="Origens de lead"
        help="De onde o contato chegou até a IGM. Ajuda a saber qual canal traz mais clientes."
        descricao="De onde os leads chegam (Instagram, indicação, prospecção…)."
        lista="origens-lead"
        itens={(origens.data ?? []).map((o) => ({ id: o.id, nome: o.nome, ativo: o.ativo }))}
      />
      <ListaEditor
        titulo="Status de lead"
        help="As colunas da tela de Leads. O status marcado como “libera proposta” é o que mostra o botão Gerar proposta no lead."
        descricao="Colunas da tela de Leads. O status Qualificado libera o Gerar proposta."
        lista="lead-status"
        itens={(leadStatus.data ?? []).map((s) => ({
          id: s.id,
          nome: s.nome,
          extra: s.permiteProposta ? 'libera proposta' : s.isDescarte ? 'descarte' : undefined,
          ativo: s.ativo,
        }))}
        semCriar
      />
      <ListaEditor
        titulo="Tipos de atividade"
        help="Os tipos de registro que aparecem na linha do tempo do card: ligação, e-mail, visita, WhatsApp… O lembrete padrão de cada tipo (quanto tempo antes avisar) não se configura aqui — fica em Configurações › Comercial › Tempos de lembrete."
        // C-5: o padrão por tipo existe e funciona, mas mora na aba Lembretes.
        // Quem procura vem parar aqui primeiro — inclusive o handoff desta
        // rodada procurou neste catálogo. O ponteiro evita a busca perdida.
        descricao="Ligação, e-mail, visita, WhatsApp, nota… O lembrete padrão de cada tipo fica em Tempos de lembrete."
        lista="tipos-atividade"
        // C-5: mostra o padrão de cada tipo AQUI, onde a pessoa olha a lista.
        // A função estava ligada e vazia e a tela não dizia nada — quem abria
        // este catálogo não tinha como saber que existia padrão nenhum.
        itens={(tipos.data ?? []).map((t) => ({
          id: t.id,
          nome: t.nome,
          extra:
            t.lembreteDefaultMinutos != null
              ? rotuloMinutos(t.lembreteDefaultMinutos)
              : SEM_LEMBRETE_PADRAO,
          ativo: t.ativo,
          icone: t.icone,
        }))}
        // LEAD-TELA (05/08): o tipo ganha SÍMBOLO (grade de 14, mockup
        // aprovado) — aparece no seletor do lead/card e na linha do tempo.
        comSimbolo
      />
      <ListaEditor
        titulo="Motivos de perda"
        help="Ao marcar um card como Perdido, o vendedor escolhe um destes motivos. Isso vira relatório de por que as vendas não fecham."
        descricao="Obrigatório ao marcar Perdido — alimenta o relatório de perdas."
        lista="motivos-perda"
        itens={(motivosPerda.data ?? []).map((m) => ({ id: m.id, nome: m.nome, ativo: m.ativo }))}
      />
      <MotivosDescarteEditor motivos={motivosDescarte.data ?? []} />
      <ListaEditor
        titulo="Documentos exigidos pro Ganho"
        help="Antes de marcar um card como Ganho, o sistema confere se estes documentos estão anexados. Serve pra ninguém fechar venda sem papelada."
        descricao="A trava do Ganho exige estes documentos anexados no card."
        lista="documentos-exigidos"
        itens={(documentos.data ?? []).map((d) => ({ id: d.id, nome: d.nome, ativo: d.ativo }))}
      />
      <ListaEditor
        titulo="Tags"
        help="Etiquetas livres pra marcar cards no funil (ex.: “urgente”, “licitação”). Só organização visual — não mudam o fluxo."
        descricao="Etiquetas dos cards do Funil."
        lista="tags"
        itens={(tags.data ?? []).map((t) => ({ id: t.id, nome: t.nome, ativo: t.ativo }))}
      />
    </div>
  );
}

interface ItemLista {
  id: string;
  nome: string;
  extra?: string;
  ativo: boolean;
  /** LEAD-TELA: slug do símbolo (só as listas com `comSimbolo`). */
  icone?: string;
}

function ListaEditor(props: {
  titulo: string;
  descricao: string;
  /** H1: explicação de leigo no "?" ao lado do título. */
  help?: string;
  lista: string;
  itens: ItemLista[];
  camposExtras?: { key: string; label: string; tipo: 'select'; opcoes: [string, string][] }[];
  semCriar?: boolean;
  /**
   * LEAD-TELA (05/08): a lista tem SÍMBOLO por item (tipos de atividade).
   * A linha mostra o símbolo, e criar/editar ganham a grade de 14 opções.
   * Sem escolha, o item nasce com o símbolo de nota (padrão do servidor).
   */
  comSimbolo?: boolean;
}) {
  const acoes = useConfigActions(props.lista);
  /**
   * ⚠️ O diálogo canônico, ❌ não um `window.confirm`: ele respeita o tema, o
   * foco e o celular — e é o mesmo que o Ganho já usa no funil.
   */
  const confirmarTroca = useConfirmDialog();
  const toast = useToast();
  const [novoNome, setNovoNome] = useState('');
  const [extras, setExtras] = useState<Record<string, string>>({});
  const [novoSimbolo, setNovoSimbolo] = useState(SIMBOLO_PADRAO);
  const [editando, setEditando] = useState<ItemLista | null>(null);
  const [nomeEditado, setNomeEditado] = useState('');
  const [simboloEditado, setSimboloEditado] = useState(SIMBOLO_PADRAO);

  // I-1: o `<Select>` EXIBIA a primeira opção sem nunca gravá-la — quem
  // aceitava o que estava na tela mandava o POST sem `tipo`, que é obrigatório,
  // e levava um 400 traduzido como "nome duplicado?". A prova está nos dados do
  // beta: os dois únicos serviços já criados pela tela são `recorrente`, o
  // valor que obriga a mexer no select. Nenhum `pontual` jamais passou.
  const extrasEfetivos = {
    ...Object.fromEntries((props.camposExtras ?? []).map((c) => [c.key, c.opcoes[0][0]])),
    ...extras,
  };

  async function criar() {
    if (!novoNome.trim()) return;
    try {
      await acoes.criar.mutateAsync({
        nome: novoNome.trim(),
        ...extrasEfetivos,
        ...(props.comSimbolo ? { icone: novoSimbolo } : {}),
      });
      setNovoNome('');
      setExtras({});
      setNovoSimbolo(SIMBOLO_PADRAO);
      toast.show('Adicionado.');
    } catch (err) {
      toast.show(mensagemErroConfig(err, `Já existe um item chamado "${novoNome.trim()}" nesta lista.`), {
        variant: 'error',
      });
    }
  }

  // H5 (auditoria mobile): min-w-0 impede o card de estourar a coluna do
  // grid quando o form interno tem largura mínima maior que o viewport.
  return (
    <Card variant="flat" className="p-4 flex flex-col min-w-0">
      <h3 className="text-base font-bold text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-1.5">
        {props.titulo}
        {props.help && <HelpTip texto={props.help} />}
      </h3>
      <p className="text-sm text-ink-3 dark:text-ink-dark-3 mt-0.5 mb-3 min-h-10">{props.descricao}</p>
      <div className="space-y-1.5 mb-3 max-h-72 overflow-y-auto pr-0.5">
        {props.itens.map((item) => (
          // FASE-C: a fileira quebra em duas linhas no celular (antes estourava
          // +52px) e o rótulo do tipo virou <Badge> — o texto não gruda mais
          // no nome ("MonitoramentoRecorrenterenomear").
          <div
            key={item.id}
            className={cn(
              'flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-lg border border-line-soft dark:border-line-soft-dark px-3 py-2',
              !item.ativo && 'opacity-50',
            )}
          >
            {props.comSimbolo && (
              <span
                aria-hidden="true"
                className="w-[26px] h-[26px] rounded-lg inline-flex items-center justify-center shrink-0 bg-line-soft dark:bg-line-soft-dark text-ink-2 dark:text-ink-dark-2"
              >
                <SimboloAtividade icone={item.icone} />
              </span>
            )}
            {/* 🔴 A5 (25/09/2026) — O NOME PARA DE SER ESPREMIDO.
                **Matheus:** *"essa tela em configurações está horrível a
                visualização… tem vários quadros e todos eles estão quebrados"*,
                com o print mostrando *"Indi/caç/ão"* e *"Pro/spe/cção"*.

                ⚠️ **MEDIDO no beta, e a causa NÃO era o modo de quebra:**
                `word-break` é `normal` e `overflow-wrap` é `break-word` — o
                certo. 🔴 **O que estava errado era a LARGURA:** com `min-w-0`
                este nome era o ÚNICO item da fileira que podia encolher (o
                símbolo, o selo e as ações são `shrink-0`), então ele ficava com
                a sobra. **Medido em 1366×768: "Qualificado" recebia 11px e
                quebrava em NOVE linhas; "Monitoramento", 36px e quatro.**

                ✅ A fileira já era `flex-wrap` — ela só nunca quebrava porque,
                com mínimo ZERO, "cabia tudo". Com um mínimo de verdade, quem
                cede passa a ser as AÇÕES, que descem para a linha de baixo:
                é exatamente o comportamento que o comentário da FASE-C acima
                descreve para o celular, agora valendo também no cartão estreito.

                ⚠️ **`ch` e não `px`:** a régua é "caber uma palavra", e palavra
                se mede em caracteres — em `px` ela quebraria de novo se alguém
                aumentasse a fonte. */}
            <span className="flex-1 min-w-[10ch] break-words text-sm font-medium text-ink-1 dark:text-ink-dark-1">
              {item.nome}
            </span>
            {/**
              * 🔴 DESATIVADO SE DIZ, ❌ não se insinua (02/10/2026).
              *
              * ⚠️ A linha já vinha com meia opacidade — e opacidade **não é
              * rótulo**: ela não é lida por leitor de tela, some num monitor
              * claro e não diz o que significa. 🔴 Sem a palavra, trocaríamos
              * *"o item sumiu"* por *"o item parece ativo e não é"* — que é
              * pior, porque ninguém desconfia.
              */}
            {!item.ativo && (
              <Badge variant="neutral" size="sm" className="shrink-0" data-testid="selo-desativado">
                desativado
              </Badge>
            )}
            {item.extra && (
              <Badge variant="neutral" size="sm" className="shrink-0">
                {item.extra}
              </Badge>
            )}
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1 shrink-0">
              <button
                type="button"
                className="inline-flex min-h-[44px] items-center text-xs text-ink-3 dark:text-ink-dark-3 hover:text-red"
                onClick={() => {
                  setEditando(item);
                  setNomeEditado(item.nome);
                  setSimboloEditado(item.icone ?? SIMBOLO_PADRAO);
                }}
              >
                {props.comSimbolo ? 'editar' : 'renomear'}
              </button>
              {/**
                * 🔴 02/10/2026 — ESTE CLIQUE PODE BLOQUEAR VENDA, e por isso ele
                * PERGUNTA antes.
                *
                * **Pedido do Matheus:** *"interruptor que bloqueia venda não pode
                * ser um clique silencioso."*
                *
                * ⚠️ Ligar um documento exigido do Ganho significa que **nenhum
                * card vai para Ganho sem ele** — e quem clica em "reativar" numa
                * lista de configuração não está pensando no vendedor que vai
                * esbarrar nisso numa sexta à tarde.
                *
                * ✅ A pergunta é **só** nesta lista. 🔴 Pôr confirmação em todas
                * as oito seria ruído: desativar uma tag não bloqueia nada, e
                * *trava que pergunta demais morre ignorada no primeiro dia.*
                */}
              <button
                type="button"
                className="inline-flex min-h-[44px] items-center text-xs text-ink-3 dark:text-ink-dark-3 hover:text-red"
                data-testid={`liga-desliga-${item.id}`}
                onClick={async () => {
                  if (props.lista === 'documentos-exigidos') {
                    const ok = await confirmarTroca({
                      title: item.ativo
                        ? `Deixar de exigir "${item.nome}" no Ganho?`
                        : `Exigir "${item.nome}" para marcar Ganho?`,
                      message: item.ativo
                        ? `As propostas voltam a ser marcadas como Ganho sem este documento. Os documentos já ligados nos cards não são apagados.`
                        : `A partir de agora, NENHUM card vai para Ganho sem o link de "${item.nome}" ligado na aba Documentos. Quem não souber vai descobrir sendo barrado no meio de uma negociação — avise a equipe.`,
                      confirmLabel: item.ativo ? 'Deixar de exigir' : 'Exigir',
                    });
                    if (!ok) return;
                  }
                  acoes.editar.mutate(
                    { id: item.id, ativo: !item.ativo },
                    {
                      onError: () =>
                        toast.show(
                          item.ativo ? 'Não foi possível desativar o item.' : 'Não foi possível reativar o item.',
                          { variant: 'error' },
                        ),
                    },
                  );
                }}
              >
                {item.ativo ? 'desativar' : 'reativar'}
              </button>
            </span>
          </div>
        ))}
        {props.itens.length === 0 && <p className="text-sm text-ink-3 dark:text-ink-dark-3">Lista vazia.</p>}
      </div>
      {!props.semCriar && (
        <div className="flex flex-wrap gap-2">
          <Input
            size="sm"
            wrapperClassName="flex-1 min-w-[120px]"
            placeholder="Novo item…"
            value={novoNome}
            onChange={(e) => setNovoNome(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && criar()}
          />
          {props.camposExtras?.map((c) => (
            <Select
              key={c.key}
              size="sm"
              value={extrasEfetivos[c.key]}
              onChange={(e) => setExtras({ ...extras, [c.key]: e.target.value })}
              wrapperClassName="w-36 shrink-0"
              aria-label={c.label}
            >
              {c.opcoes.map(([v, l]) => (
                <SelectOption key={v} value={v}>
                  {l}
                </SelectOption>
              ))}
            </Select>
          ))}
          <Button size="sm" variant="secondary" onClick={criar} disabled={!novoNome.trim()}>
            Adicionar
          </Button>
          {props.comSimbolo && (
            <div className="w-full">
              <Label className="text-[11px]">Símbolo do novo tipo</Label>
              <GradeSimbolos valor={novoSimbolo} onEscolher={setNovoSimbolo} testId="grade-simbolos-criar" />
              <p className="text-[11px] text-ink-3 dark:text-ink-dark-3 mt-1">
                14 opções — poucos e bons. Sem escolha, o tipo nasce com o símbolo de nota.
              </p>
            </div>
          )}
        </div>
      )}

      <Dialog open={Boolean(editando)} onOpenChange={(o) => !o && setEditando(null)} size="sm">
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{props.comSimbolo ? 'Editar tipo' : 'Renomear'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input value={nomeEditado} onChange={(e) => setNomeEditado(e.target.value)} aria-label="Nome" />
            {props.comSimbolo && (
              <div>
                <Label className="text-[11px]">Símbolo</Label>
                <GradeSimbolos valor={simboloEditado} onEscolher={setSimboloEditado} />
                <p className="text-[11px] text-ink-3 dark:text-ink-dark-3 mt-1">
                  Aparece no seletor de tipo do lead e do card e na linha do tempo.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditando(null)}>
              Cancelar
            </Button>
            <Button
              onClick={async () => {
                if (!editando) return;
                try {
                  await acoes.editar.mutateAsync({
                    id: editando.id,
                    nome: nomeEditado,
                    ...(props.comSimbolo ? { icone: simboloEditado } : {}),
                  });
                  setEditando(null);
                } catch (err) {
                  toast.show(
                    mensagemErroConfig(err, `Já existe um item chamado "${nomeEditado.trim()}" nesta lista.`),
                    { variant: 'error' },
                  );
                }
              }}
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── Motivos de descarte (com prazo de retorno por motivo — PF11) ──────

/**
 * Editor dedicado dos motivos de descarte: além de add/renomear/desativar,
 * cada motivo tem um PRAZO DE RETORNO (mig 392). Passado o prazo, o lead
 * descartado volta sozinho pra "Novo". "Não retorna" = fica descartado. O
 * destino (mesmo vendedor × pool) é global, no topo do cartão.
 */
function MotivosDescarteEditor(props: { motivos: MotivoSimples[] }) {
  const acoes = useConfigActions('motivos-descarte');
  const destinoQuery = useReciclagemDestino();
  const salvarDestino = useSalvarReciclagemDestino();
  const toast = useToast();
  const [novoNome, setNovoNome] = useState('');
  const [editando, setEditando] = useState<MotivoSimples | null>(null);
  const [nomeEditado, setNomeEditado] = useState('');

  async function criar() {
    if (!novoNome.trim()) return;
    try {
      await acoes.criar.mutateAsync({ nome: novoNome.trim() });
      setNovoNome('');
      toast.show('Adicionado.');
    } catch (err) {
      toast.show(mensagemErroConfig(err, `Já existe um motivo chamado "${novoNome.trim()}".`), {
        variant: 'error',
      });
    }
  }

  return (
    <Card variant="flat" className="p-4 flex flex-col min-w-0">
      <h3 className="text-base font-bold text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-1.5">
        Motivos de descarte
        <HelpTip texto="Descartar = o lead nem virou negociação (ex.: contato errado, sem interesse). O motivo escolhido fica registrado. Cada motivo pode fazer o lead voltar pra Novo depois de um prazo — ou nunca (não retorna)." />
      </h3>
      <p className="text-sm text-ink-3 dark:text-ink-dark-3 mt-0.5 mb-3 min-h-10">
        Obrigatório ao descartar um lead. O prazo faz o lead voltar sozinho pra prospecção.
      </p>

      {/* Destino global do retorno (mesmo vendedor × pool) */}
      <div className="rounded-lg border border-line-soft dark:border-line-soft-dark px-3 py-2.5 mb-3">
        <span className="flex items-center gap-1.5 mb-1.5">
          <Label htmlFor="retorno-destino" className="mb-0">Quando o lead volta, vai para</Label>
          <HelpTip texto="Mesmo vendedor: volta pra quem descartou. Pool: entra na lista de Disponíveis pra qualquer vendedor pegar." />
        </span>
        <Select
          id="retorno-destino"
          size="sm"
          value={destinoQuery.data ?? 'mesmo_vendedor'}
          disabled={destinoQuery.isLoading}
          onChange={(e) =>
            salvarDestino.mutate(e.target.value as RetornoDescarteDestino, {
              onError: () => toast.show('Não foi possível salvar o destino.', { variant: 'error' }),
            })
          }
          className="w-full"
        >
          <SelectOption value="mesmo_vendedor">Mesmo vendedor (quem descartou)</SelectOption>
          <SelectOption value="pool">Pool — Disponíveis pra qualquer vendedor</SelectOption>
        </Select>
      </div>

      <div className="space-y-1.5 mb-3 max-h-72 overflow-y-auto pr-0.5">
        {props.motivos.map((m) => (
          <div
            key={m.id}
            className={cn(
              'rounded-lg border border-line-soft dark:border-line-soft-dark px-3 py-2',
              !m.ativo && 'opacity-50',
            )}
          >
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
              {/* 🔴 A5 — o IRMÃO da fileira de cima, mesmo defeito, mesma cura.
                  ⚠️ Achado pelo teste, não pela leitura: "correção em componente
                  canônico obriga a varrer os irmãos" — e aqui o irmão estava no
                  mesmo arquivo, 220 linhas abaixo. */}
              <span className="flex-1 min-w-[10ch] break-words text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                {m.nome}
              </span>
              {/* 🔴 O IRMÃO do selo de cima, mesmo defeito, mesma cura — e de
                  novo 250 linhas abaixo, no mesmo arquivo. ⚠️ Achado pelo
                  `grep` por `!.*ativo && 'opacity-50'`, ❌ não de memória. */}
              {!m.ativo && (
                <Badge variant="neutral" size="sm" className="shrink-0" data-testid="selo-desativado">
                  desativado
                </Badge>
              )}
              <span className="flex flex-wrap items-center gap-x-3 gap-y-1 shrink-0">
                <button
                  type="button"
                  className="inline-flex min-h-[44px] items-center text-xs text-ink-3 dark:text-ink-dark-3 hover:text-red"
                  onClick={() => {
                    setEditando(m);
                    setNomeEditado(m.nome);
                  }}
                >
                  renomear
                </button>
                <button
                  type="button"
                  className="inline-flex min-h-[44px] items-center text-xs text-ink-3 dark:text-ink-dark-3 hover:text-red"
                  onClick={() =>
                    acoes.editar.mutate(
                      { id: m.id, ativo: !m.ativo },
                      {
                        onError: () =>
                          toast.show(
                            m.ativo ? 'Não foi possível desativar o item.' : 'Não foi possível reativar o item.',
                            { variant: 'error' },
                          ),
                      },
                    )
                  }
                >
                  {m.ativo ? 'desativar' : 'reativar'}
                </button>
              </span>
            </div>
            {m.ativo && (
              <PrazoRetorno
                valor={m.prazoRetornoDias ?? null}
                onSalvar={(prazo) =>
                  acoes.editar.mutate(
                    { id: m.id, prazoRetornoDias: prazo },
                    { onError: () => toast.show('Não foi possível salvar o prazo.', { variant: 'error' }) },
                  )
                }
              />
            )}
          </div>
        ))}
        {props.motivos.length === 0 && <p className="text-sm text-ink-3 dark:text-ink-dark-3">Lista vazia.</p>}
      </div>

      <div className="flex flex-wrap gap-2">
        <Input
          size="sm"
          wrapperClassName="flex-1 min-w-[120px]"
          placeholder="Novo motivo…"
          value={novoNome}
          onChange={(e) => setNovoNome(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && criar()}
        />
        <Button size="sm" variant="secondary" onClick={criar} disabled={!novoNome.trim()}>
          Adicionar
        </Button>
      </div>

      <Dialog open={Boolean(editando)} onOpenChange={(o) => !o && setEditando(null)} size="sm">
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renomear</DialogTitle>
          </DialogHeader>
          <Input value={nomeEditado} onChange={(e) => setNomeEditado(e.target.value)} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditando(null)}>
              Cancelar
            </Button>
            <Button
              onClick={async () => {
                if (!editando) return;
                try {
                  await acoes.editar.mutateAsync({ id: editando.id, nome: nomeEditado });
                  setEditando(null);
                } catch (err) {
                  toast.show(
                    mensagemErroConfig(err, `Já existe um item chamado "${nomeEditado.trim()}" nesta lista.`),
                    { variant: 'error' },
                  );
                }
              }}
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/**
 * Controle do prazo de retorno de UM motivo. Vazio = "não retorna" (null).
 * Salva no blur/Enter; valor inválido volta pro atual sem salvar.
 */
function PrazoRetorno(props: { valor: number | null; onSalvar: (prazo: number | null) => void }) {
  const { valor, onSalvar } = props;
  const [naoRetorna, setNaoRetorna] = useState(valor === null);
  const [texto, setTexto] = useState(valor === null ? '' : String(valor));

  useEffect(() => {
    setNaoRetorna(valor === null);
    setTexto(valor === null ? '' : String(valor));
  }, [valor]);

  const confirmar = () => {
    const limpo = texto.trim();
    const n = Number(limpo);
    if (limpo === '' || !Number.isFinite(n) || n < 1) {
      // valor inválido no modo "retorna" → volta pro atual
      setTexto(valor === null ? '' : String(valor));
      return;
    }
    const novo = Math.floor(n);
    setTexto(String(novo));
    if (novo !== valor) onSalvar(novo);
  };

  return (
    <div className="flex items-center gap-2 mt-2 flex-wrap">
      <span className="text-xs text-ink-3 dark:text-ink-dark-3 inline-flex items-center gap-1">
        Volta pra Novo
        <HelpTip texto="Dias corridos após o descarte para o lead voltar sozinho pra prospecção. Escolha 'Não retorna' para o lead ficar descartado." />
      </span>
      <Select
        size="sm"
        value={naoRetorna ? 'nao' : 'sim'}
        onChange={(e) => {
          const retorna = e.target.value === 'sim';
          setNaoRetorna(!retorna);
          if (!retorna) {
            setTexto('');
            if (valor !== null) onSalvar(null);
          } else {
            // liga o modo "retorna" com um default de 30 dias
            const inicial = valor ?? 30;
            setTexto(String(inicial));
            if (valor === null) onSalvar(inicial);
          }
        }}
        wrapperClassName="w-32 shrink-0"
        aria-label="Retorno do descarte"
      >
        <SelectOption value="nao">Não retorna</SelectOption>
        <SelectOption value="sim">Em X dias corridos</SelectOption>
      </Select>
      {!naoRetorna && (
        <span className="inline-flex items-center gap-1.5">
          <Input
            size="sm"
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onBlur={confirmar}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                confirmar();
              }
            }}
            wrapperClassName="w-20 shrink-0"
            aria-label="Dias corridos para voltar"
          />
          {/* CA-11a: deixar explícito que o prazo conta TODO dia (fim de semana junto). */}
          <span className="text-xs text-ink-3 dark:text-ink-dark-3 inline-flex items-center gap-1">
            dias corridos
            <HelpTip texto="Conta todo dia do calendário, incluindo fins de semana e feriados." />
          </span>
        </span>
      )}
    </div>
  );
}

// ─── Aba: Fases + campos obrigatórios ──────────────────────────────────

